import type { ButtonProps } from 'antd';
import { Button, Col, Form, Input, Popover, Row } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DatePicker, FilterIcon, Select } from '@/components';
import type { TrackEventFunction } from '@/hooks/useTrackEvents/types';

import { buildFilterParams } from './helpers';
import * as Styled from './styles';
import type { Input as FilterInput, Filters } from './types';

export type Props = {
  title?: string;
  config: Record<string, FilterInput>;
  filters?: Filters;
  onFilter?: (filters?: Filters) => void;
  onClearFilter?: VoidFunction;
  trackEvent?: TrackEventFunction;
  buttonProps?: ButtonProps;
};

const FilterButton = ({
  title,
  config = {},
  filters = {},
  onFilter,
  onClearFilter,
  trackEvent,
  buttonProps
}: Props) => {
  const [form] = Form.useForm<Record<string, string | string[]>>();
  const [isOpen, setIsOpen] = useState(false);
  const wasOpenRef = useRef(false);

  const handleClear = useCallback(() => {
    trackEvent?.('click to clear all filters');
    onFilter?.(undefined);
    onClearFilter?.();
    setIsOpen(false);
    form.resetFields();
  }, [form, onClearFilter, onFilter, trackEvent]);

  const handleFilter = useCallback(() => {
    if (!form.isFieldsTouched()) {
      setIsOpen(false);
      return;
    }
    const values = form.getFieldsValue() as Record<string, unknown>;
    const params = buildFilterParams(values, config);

    trackEvent?.('click to apply filters');
    onFilter?.(params);
    setIsOpen(false);
  }, [form, onFilter, trackEvent, config]);

  /**
   * Seeds the form from the applied `filters` on the opening transition only.
   *
   * `filters` and `config` are fresh objects on renders that say nothing about
   * what is applied — a list refetch settling, a filter-options response
   * arriving — so re-seeding on those would overwrite what the user is typing.
   * They stay in the deps and simply trigger a run that returns early; the
   * guard is the transition, not the dep list.
   *
   * The seed cannot move to a while-closed sync: closing is itself the Clear
   * action, which would re-fill the form the user just emptied, and before the
   * first open the popover has mounted no `Form` for this instance to write to.
   *
   * Opening is also what discards an abandoned session — `resetFields` runs
   * first, so `isFieldsTouched()` reads false again for an untouched form.
   */
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!justOpened) return;

    form.resetFields();

    if (!Object.keys(filters).length) return;

    const values: Record<string, string | string[] | Dayjs> = {};

    Object.entries(filters).forEach(([key, filterValues]) => {
      const filterConfig = config[key];

      if (filterConfig?.type === 'select') {
        values[key] = filterValues;
      } else if (filterConfig?.type === 'date') {
        values[key] = dayjs(filterValues[0]);
      } else {
        values[key] = filterValues[0];
      }
    });

    form.setFieldsValue(
      values as Record<string, string | string[] | undefined>
    );
  }, [filters, config, form, isOpen]);

  return (
    <Popover
      title={title}
      placement='bottomLeft'
      arrow={false}
      trigger='click'
      content={
        <Styled.Container>
          <Styled.FormContainer>
            <Form form={form} layout='vertical'>
              {Object.entries(config).map(([key, filterConfig]) => {
                let input = <Input />;

                if (filterConfig.type === 'select') {
                  input = (
                    <Select
                      options={filterConfig.options}
                      isMultiple={filterConfig.multiple}
                    />
                  );
                } else if (filterConfig.type === 'date') {
                  input = <DatePicker style={{ width: '100%' }} />;
                }
                return (
                  <Form.Item
                    key={key}
                    label={filterConfig?.title ?? key}
                    name={key}>
                    {input}
                  </Form.Item>
                );
              })}
            </Form>
          </Styled.FormContainer>
          <Row gutter={8}>
            <Col span={12}>
              <Button block={true} onClick={handleClear}>
                Clear
              </Button>
            </Col>
            <Col span={12}>
              <Button
                block={true}
                color='default'
                variant='solid'
                onClick={handleFilter}>
                Apply
              </Button>
            </Col>
          </Row>
        </Styled.Container>
      }
      open={isOpen}
      onOpenChange={setIsOpen}>
      <Button
        data-testid='filter-button'
        icon={<FilterIcon />}
        {...buttonProps}
      />
    </Popover>
  );
};

export default FilterButton;
